// Gating de features por grupo de testeo (`company_profiles.grupo_testing`).
//
// Fuente de verdad única para "quién ve qué". Tres funciones puras que usan
// tanto el Sidebar (desktop), la barra inferior (mobile), el landing inicial y
// los botones de navegación, así todos coinciden siempre.
//
// Modelo:
//  · dev_tester = true  → ve TODO (gana sobre el grupo).
//  · grupo_testing 'A' (o desconocido) → flujo clásico (Estudio Virtual →
//    seleccionar productos → "prueba nueva interfaz").
//  · grupo_testing 'B' → flujo V2 (Interfaz V2 + Modos).
//  · grupo_testing 'C' → lo mismo que B, y además la INTERFAZ NUEVA de escritorio: la
//    home por destino y la sidebar por secciones (ADR-2026-10-07-grupo-c-ve-la-interfaz-nueva).
//    Desde el 2026-10-17 toda alta nueva nace en C (ADR-2026-10-17-las-altas-nuevas-nacen-en-c);
//    las cuentas que ya existían siguen en su grupo.

export const GRUPO_A = "A";
export const GRUPO_B = "B";
/** Grupo C: la interfaz nueva. El onboarding del backend lo escribe en cada alta desde el
 *  2026-10-17; a una cuenta vieja se la pasa a mano (`UPDATE company_profiles`). */
export const GRUPO_C = "C";

/** Los grupos que viven en la Interfaz V2: B, y C, que es "B con la interfaz nueva".
 *  Exportado para los lugares que comparaban `grupo === GRUPO_B` a mano (la sidebar vieja
 *  de mobile, Ayuda): ahí C también cuenta como B. */
export function esGrupoV2(grupo: string | null | undefined): boolean {
  return grupo === GRUPO_B || grupo === GRUPO_C;
}

/**
 * La home por destino y la sidebar por secciones (ADR-2026-10-07-grupo-c-ve-la-interfaz-nueva).
 * Desconocido (`null`, fallo de red, primer login) → `false`: la UI de siempre. No mira
 * `dev_tester` a propósito: los testers entran a C por la columna, como cualquier cuenta.
 */
export function usaInterfazNueva(grupo: string | null | undefined): boolean {
  return grupo === GRUPO_C;
}

/** Home del flujo clásico (Estudio Virtual → seleccionar productos). */
export const CLASSIC_STUDIO_PATH =
  "/redes-sociales/crear-anuncio/seleccionar-productos";
/** Home del flujo V2 (Interfaz V2). */
export const V2_STUDIO_PATH = "/interfaz-v2";

/**
 * Experiencia V2 (Interfaz V2 + Modos) en nav/tabs/acciones.
 * dev ve todo → true; Grupo B y C → true; Grupo A (o desconocido) → false.
 */
export function showsV2Studio(
  isDevTester: boolean | null | undefined,
  grupo: string | null | undefined,
): boolean {
  return isDevTester === true || esGrupoV2(grupo);
}

/**
 * Experiencia clásica (Estudio Virtual → seleccionar productos) en el nav.
 * dev ve todo → true; Grupo A (o desconocido) → true; Grupo B y C → false.
 */
export function showsClassicStudio(
  isDevTester: boolean | null | undefined,
  grupo: string | null | undefined,
): boolean {
  return isDevTester === true || !esGrupoV2(grupo);
}

/**
 * Landing inicial (mobile) y retornos: **dev y Grupo B → V2; Grupo A y grupo
 * desconocido → clásico**.
 *
 * El dev arrancaba en el flujo CLÁSICO, distinto a propósito de `showsV2Studio`: "ve el
 * nav V2 pero su landing sigue siendo el clásico, igual que hoy". Ese "igual que hoy"
 * era de cuando V2 estaba en prueba; desde ADR-2026-07-31-inicio-se-funde-interfaz-V2 el
 * desktop manda a TODOS a V2 y el que se quedó atrás fue mobile. El resultado era que
 * quien más entra a probar —la cuenta dev— aterrizaba en la pantalla vieja de seleccionar
 * productos, y lo mismo pasaba en cada retorno vía `entryStudioPath`: terminar de crear
 * una marca ya había tenido que esquivar esta función a mano
 * (ADR-2026-08-04-la-marca-nueva-arranca-vacia). Ver
 * ADR-2026-09-10-el-landing-de-mobile-no-manda-al-dev-al-flujo-viejo.
 *
 * Grupo A y las cuentas sin grupo NO cambian: son clientes reales cuyo producto es el
 * flujo clásico. Lo único que se movió es la excepción del dev.
 *
 * Queda como función aparte de `showsV2Studio` —hoy dan lo mismo— porque son dos
 * preguntas distintas (dónde aterrizás / qué ve el nav) y tienen 10 call-sites entre las
 * dos: fusionarlas ata un cambio de landing a un cambio de navegación.
 */
export function landingIsV2(
  isDevTester: boolean | null | undefined,
  grupo: string | null | undefined,
): boolean {
  return showsV2Studio(isDevTester, grupo);
}

/**
 * Destino del "home de estudio" tras generar imágenes (botones "Listo" /
 * "Seguir generando"). dev y Grupo B → V2; Grupo A → clásico (usa
 * `showsV2Studio`, igual que el botón "Listo" histórico).
 */
export function keepGeneratingStudioPath(
  isDevTester: boolean | null | undefined,
  grupo: string | null | undefined,
): string {
  return showsV2Studio(isDevTester, grupo) ? V2_STUDIO_PATH : CLASSIC_STUDIO_PATH;
}

/**
 * Destino del "home de estudio" para entradas/onboarding/retornos (post-
 * onboarding, "volver a config"). Solo Grupo B → V2; dev y Grupo A → clásico
 * (usa `landingIsV2`, igual que el landing de `/`).
 */
export function entryStudioPath(
  isDevTester: boolean | null | undefined,
  grupo: string | null | undefined,
): string {
  return landingIsV2(isDevTester, grupo) ? V2_STUDIO_PATH : CLASSIC_STUDIO_PATH;
}
